import { handleActions } from 'redux-actions';
import { getDreamupPrompt } from '@wix/da-shared-react/pkg/types/deviation/getters';
import { dreamupGenerationFailed } from '@wix/da-shared-react/pkg/redux/dreamup/actions';
import { reduxKey } from './constants';
import {
  ArtStyle,
  FormStoreSlice,
  SubmissionStatus,
  VariationSource,
} from './types';
import {
  changeArtStyle,
  changeAspectRatio,
  changeNegativePrompt,
  changePrompt,
  changePromptStrength,
  changeSimilarity,
  clearPrompt,
  clearVariation,
  generateFromPrompt,
  generationFromPromptInitiationFailed,
  generationFromPromptInitiationSucceeded,
  hidePromptSettingsPanel,
  hideStartingImageSelector,
  initializeForm,
  showPromptSettingsPanel,
  showStartingImageSelector,
  startVariation,
  toggleNegativePrompt,
  updateRestrictions,
} from './actions';

const defaultFormValues: FormStoreSlice['formValues'] = {
  aspectRatio: '1:1',
  similarity: 50,
  prompt: '',
  promptStrength: 7,
  artStyle: ArtStyle.Default,
  isNegativePromptInputVisible: false,
  negativePrompt: '',
};

const reducers = handleActions<FormStoreSlice, any>(
  {
    [`${initializeForm}`]: (
      state,
      { payload: { data, options } }: ReturnType<typeof initializeForm>
    ) => ({
      ...state,
      restrictions: data.restrictions,
      pointsPackages: data.pointsPackages.map(pointsPackage => ({
        points: pointsPackage.pricePoints,
        price: pointsPackage.priceDollars,
        subproductId: pointsPackage.subproductId,
      })),
      isPromptSettingsPanelVisible: options.expandSettings,
      shouldPullFormValuesFromLocalStorage: options.persistFromStorage,
      formValues: {
        ...state.formValues,
        // The only default returned by the backend is `promptStrength`.
        // (Backend calls it `guidance_scale`, if you need to find it)
        promptStrength: data.defaultFormValues?.promptStrength
          ? data.defaultFormValues?.promptStrength
          : state.formValues.promptStrength,
        prompt: options.initialPrompt,
      },
    }),
    [`${changePrompt}`]: (state, action: ReturnType<typeof changePrompt>) => ({
      ...state,
      formValues: {
        ...state.formValues,
        prompt: action.payload.prompt,
      },
    }),
    [`${toggleNegativePrompt}`]: state => ({
      ...state,
      formValues: {
        ...state.formValues,
        negativePrompt: '',
        isNegativePromptInputVisible:
          !state.formValues.isNegativePromptInputVisible,
      },
    }),
    [`${changeNegativePrompt}`]: (
      state,
      action: ReturnType<typeof changeNegativePrompt>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        negativePrompt: action.payload.negativePrompt,
      },
    }),
    [`${clearPrompt}`]: state => ({
      ...state,
      formValues: {
        ...state.formValues,
        prompt: '',
      },
    }),
    [`${changeSimilarity}`]: (
      state,
      action: ReturnType<typeof changeSimilarity>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        similarity: action.payload.similarity,
      },
    }),
    [`${changePromptStrength}`]: (
      state,
      action: ReturnType<typeof changePromptStrength>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        promptStrength: action.payload.promptStrength,
      },
    }),
    [`${changeAspectRatio}`]: (
      state,
      action: ReturnType<typeof changeAspectRatio>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        aspectRatio: action.payload.aspectRatio,
      },
    }),
    [`${changeArtStyle}`]: (
      state,
      action: ReturnType<typeof changeArtStyle>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        artStyle: action.payload.artStyle,
      },
    }),
    [`${startVariation}`]: (
      state,
      action: ReturnType<typeof startVariation>
    ) => ({
      ...state,
      formValues: {
        ...state.formValues,
        // Our AI does not support alternate aspect ratios for variations
        aspectRatio: defaultFormValues.aspectRatio,
        prompt:
          action.payload.options.variationSource === VariationSource.Dreamup
            ? (getDreamupPrompt(action.payload.deviation) ?? '')
            : state.formValues.prompt,
      },
      variationTarget: action.payload.deviation,
      variationSource: action.payload.options.variationSource,
    }),
    [`${clearVariation}`]: state => ({
      ...state,
      formValues: {
        ...state.formValues,
        prompt:
          state.variationSource === VariationSource.Dreamup
            ? ''
            : state.formValues.prompt,
      },
      variationTarget: undefined,
      variationSource: undefined,
    }),
    [`${showPromptSettingsPanel}`]: state => ({
      ...state,
      isPromptSettingsPanelVisible: true,
    }),
    [`${hidePromptSettingsPanel}`]: state => ({
      ...state,
      isPromptSettingsPanelVisible: false,
    }),
    [`${generateFromPrompt}`]: state => ({
      ...state,
      currentSubmission: { status: SubmissionStatus.AwaitingResponse },
    }),
    [`${generationFromPromptInitiationSucceeded}`]: (
      state,
      action: ReturnType<typeof generationFromPromptInitiationSucceeded>
    ) => ({
      ...state,
      formValues: {
        ...defaultFormValues,
        prompt: state.formValues.prompt,
        artStyle: state.formValues.artStyle,
        promptStrength: state.formValues.promptStrength,
        aspectRatio: state.formValues.aspectRatio,
        negativePrompt: state.formValues.negativePrompt,
        isNegativePromptInputVisible:
          state.formValues.isNegativePromptInputVisible,
      },
      variationTarget: state.variationTarget,
      currentSubmission: { status: SubmissionStatus.Idle },
      restrictions: {
        ...action.payload.restrictions,
      },
    }),
    [`${generationFromPromptInitiationFailed}`]: (
      state,
      action: ReturnType<typeof generationFromPromptInitiationFailed>
    ) => ({
      ...state,
      currentSubmission: {
        status: SubmissionStatus.Error,
        error: action.payload.error,
        serverErrorMessage: action.payload.serverErrorMessage,
      },
    }),
    [`${showStartingImageSelector}`]: state => ({
      ...state,
      isStartingImageSelectorOpen: true,
    }),
    [`${hideStartingImageSelector}`]: state => ({
      ...state,
      isStartingImageSelectorOpen: false,
    }),
    [`${dreamupGenerationFailed}`]: (
      state,
      action: ReturnType<typeof dreamupGenerationFailed>
    ) => ({
      ...state,
      restrictions: {
        ...state.restrictions,
        freePromptsRemaining: action.payload.freePromptsRemaining,
      },
    }),
    [`${updateRestrictions}`]: (
      state,
      action: ReturnType<typeof updateRestrictions>
    ) => ({ ...state, restrictions: action.payload.restrictions }),
  },
  {
    currentSubmission: {
      status: SubmissionStatus.Idle,
    },
    formValues: defaultFormValues,
    isPromptSettingsPanelVisible: false,
    shouldPullFormValuesFromLocalStorage: false,
    restrictions: {
      // Mostly gibberish defaults so that types are easier to work with. This should get overridden by
      // the initializeDreamup action. Erring toward restrictive defaults as a safety measure.
      canUseFreePrompt: false,
      canUsePaidPrompt: false,
      freePromptsAllowed: 0,
      freePromptsRemaining: 0,
      imagesPerPrompt: 0,
      promptPointsPrice: 40,
      queuePriority: 'low',
    },
    pointsPackages: [],
    isStartingImageSelectorOpen: false,
  }
);

export default {
  [reduxKey]: reducers,
};
